import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { routes } from '../../../routes';
import { SkillPeopleData, SkillPeopleSkill } from '../types';

type Props = {
  data: SkillPeopleData;
};

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0 || parts[0] === '') return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

// Deep-links the Directory at one colleague, which opens its details panel —
// the same URL shape the command bar's record search already produces.
function personRoute(id: string): string {
  return `${routes.peopleDirectory.route.template()}?employeeId=${id}`;
}

const SkillBadge: React.FC<{ skill: SkillPeopleSkill }> = ({ skill }) => (
  <span className="ai-chat-widget-skill-people-badge">
    {skill.top && (
      <span
        className="ai-chat-widget-skill-people-badge-top"
        aria-hidden="true">
        ★
      </span>
    )}
    <span className="ai-chat-widget-skill-people-badge-name">{skill.name}</span>
    {skill.rate !== undefined && (
      <span className="ai-chat-widget-skill-people-badge-rate">
        {skill.rate}
      </span>
    )}
  </span>
);

const SkillPeopleWidget: React.FC<React.PropsWithChildren<Props>> = ({
  data,
}) => {
  const { t } = useTranslation();
  if (data.people.length === 0) {
    return null;
  }
  return (
    <div className="ai-chat-widget ai-chat-widget-skill-people">
      {data.people.map((person) => (
        // A real anchor with target=_blank, not the onNavigate router push the
        // other chat links use: that one closes the chat, which loses the answer
        // the cards belong to. noreferrer alongside noopener because older
        // browsers only honour the former.
        <a
          key={person.id}
          className="ai-chat-widget-skill-people-row"
          href={personRoute(person.id)}
          target="_blank"
          rel="noopener noreferrer">
          <div className="ai-chat-widget-skill-people-avatar">
            {person.avatarUrl ? (
              <img src={person.avatarUrl} alt="" />
            ) : (
              <span className="ai-chat-widget-skill-people-avatar-initials">
                {getInitials(person.name)}
              </span>
            )}
          </div>
          <div className="ai-chat-widget-skill-people-meta">
            <span className="ai-chat-widget-skill-people-name">
              {person.name}
            </span>
            {person.role && (
              <span className="ai-chat-widget-skill-people-role">
                {person.role}
              </span>
            )}
            {person.skills.length > 0 && (
              <span className="ai-chat-widget-skill-people-badges">
                {person.skills.map((skill, idx) => (
                  <SkillBadge key={`${skill.name}-${idx}`} skill={skill} />
                ))}
              </span>
            )}
          </div>
        </a>
      ))}
      {data.moreCount !== undefined && data.moreCount > 0 && (
        <span className="ai-chat-widget-skill-people-more">
          {t('aiChat.widgets.skillPeople.more', { count: data.moreCount })}
        </span>
      )}
    </div>
  );
};

export default SkillPeopleWidget;
